import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Restaurant as RestaurantIcon,
  LocalShipping as DeliveryIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import userApi from "../../api/users";
import { format, isValid, parseISO } from "date-fns";
import Layout from "../../components/layout/Layout";
import UserWallet from "./UserWallet";
import NotificationDialog from "./NotificationDialog";

interface User {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  firebaseUid?: string;
  expoPushToken?: string;
  addresses?: Array<{
    flatNumber: string;
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    postcode: string;
    city: string;
    state: string;
  }>;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Subscription {
  _id: string;
  uid: string;
  planId: {
    _id: string;
    title?: string;
    name?: string;
    price: number;
  };
  status: string;
  startDate: string;
  endDate: string;
  progress: number;
  ordersRemaining: number;
  netAmount: number;
  createdAt: string;
}

interface Order {
  _id: string;
  mealId: {
    _id: string;
    name: string;
  };
  subscriptionId: {
    _id: string;
    uid: string;
  };
  deliveryDate: string;
  status: string;
  createdAt: string;
}

interface UserStats {
  totalOrders: number;
  activeSubscriptions: number;
}

// Helper function to safely format dates
const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";

  try {
    const date = parseISO(dateString);
    return isValid(date) ? format(date, "MMM dd, yyyy") : "Invalid Date";
  } catch (error) {
    return "Invalid Date";
  }
};

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const statusColors: Record<string, "success" | "warning" | "error" | "default" | "info"> = {
  active: "success",
  paused: "warning",
  cancelled: "error",
  pending: "info",
  completed: "default",
};

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Notification dialog state
  const [notificationDialog, setNotificationDialog] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  }>({
    open: false,
    userId: "",
    userName: "",
  });

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const data = await userApi.getUserDetails(userId!);
      console.log("User details data:", data); // Debug log
      console.log("User:", data.user);
      console.log("Subscriptions:", data.subscriptions);
      console.log("Orders:", data.recentOrders);
      console.log("Stats:", data.stats);
      
      setUser(data.user);
      setSubscriptions(data.subscriptions || []);
      setRecentOrders(data.recentOrders || []);
      setStats(data.stats || null);
    } catch (err: any) {
      console.error("Error fetching user details:", err);
      setError(err.response?.data?.message || "Error fetching user details");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNotificationDialog = () => {
    if (user) {
      setNotificationDialog({
        open: true,
        userId: user._id,
        userName: user.name || "User",
      });
    }
  };

  const handleCloseNotificationDialog = () => {
    setNotificationDialog({
      open: false,
      userId: "",
      userName: "",
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <Typography>Loading user details...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error || !user) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "User not found"}
          </Alert>
          <Button
            onClick={() => navigate("/users")}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Users
          </Button>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/users")}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4">
                {user.name || "User Details"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {user._id}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* <Chip
              label={user.isActive !== undefined ? (user.isActive ? "Active" : "Inactive") : "Unknown"}
              color={user.isActive !== undefined ? (user.isActive ? "success" : "error") : "default"}
              icon={user.isActive !== undefined ? (user.isActive ? <CheckCircleIcon /> : <CancelIcon />) : <WarningIcon />}
            /> */}
            <Button
              variant="outlined"
              startIcon={<NotificationsIcon />}
              onClick={handleOpenNotificationDialog}
              size="small"
            >
              Send Notification
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <UserWallet key={user._id} userId={user._id} userName={user.name || user.phoneNumber} />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 3 }}>
          {/* User Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PersonIcon />
                User Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.name || "N/A"}
                    secondary="Full Name"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.email || "N/A"}
                    secondary="Email Address"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.phoneNumber || "N/A"}
                    secondary="Phone Number"
                  />
                </ListItem>
                {user.firebaseUid && (
                  <ListItem>
                    <ListItemIcon>
                      <AccountIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={user.firebaseUid}
                      secondary="Firebase UID"
                    />
                  </ListItem>
                )}
                {user.expoPushToken && (
                  <ListItem>
                    <ListItemIcon>
                      <NotificationsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Enabled"
                      secondary="Push Notifications"
                      sx={{ "& .MuiListItemText-primary": { color: "success.main" } }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Account Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <AccountIcon />
                Account Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={formatDate(user.createdAt)}
                    secondary="Join Date"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={formatDate(user.updatedAt)}
                    secondary="Last Updated"
                  />
                </ListItem>
                {stats && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <RestaurantIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={stats.activeSubscriptions}
                        secondary="Active Subscriptions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DeliveryIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={stats.totalOrders}
                        secondary="Total Orders"
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Addresses */}
          {user.addresses && user.addresses.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <LocationIcon />
                  Addresses ({user.addresses.length})
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 2 }}>
                  {user.addresses.map((address, index) => (
                    <Paper sx={{ p: 2, border: "1px solid #e0e0e0" }} key={index}>
                      <Typography variant="subtitle2" gutterBottom>
                        Address {index + 1}
                      </Typography>
                      <List dense>
                        {address.flatNumber && (
                          <ListItem sx={{ py: 0 }}>
                            <ListItemText
                              primary={address.flatNumber}
                              secondary="Flat/Unit Number"
                            />
                          </ListItem>
                        )}
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={address.address}
                            secondary="Street Address"
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={address.city}
                            secondary="City"
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={address.state}
                            secondary="State"
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={address.postcode}
                            secondary="Postcode"
                          />
                        </ListItem>
                        {address.coordinates && (
                          <ListItem sx={{ py: 0 }}>
                            <ListItemText
                              primary={`${address.coordinates.latitude.toFixed(6)}, ${address.coordinates.longitude.toFixed(6)}`}
                              secondary="Coordinates"
                            />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Subscriptions */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <RestaurantIcon />
                Subscriptions ({subscriptions.length})
              </Typography>
              {subscriptions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>UID</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Dates</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptions.map((subscription) => (
                        <TableRow key={subscription._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {subscription.uid}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {subscription.planId?.title || subscription.planId?.name || "N/A"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(subscription.planId?.price || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={subscription.status}
                              color={statusColors[subscription.status] || "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {subscription.progress} / {subscription.ordersRemaining}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              Start: {formatDate(subscription.startDate)}
                            </Typography>
                            <Typography variant="body2">
                              End: {formatDate(subscription.endDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(subscription.netAmount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(subscription.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No subscriptions found for this user.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <DeliveryIcon />
                Recent Orders ({recentOrders.length})
              </Typography>
              {recentOrders.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Meal</TableCell>
                        <TableCell>Subscription</TableCell>
                        <TableCell>Delivery Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrders.map((order) => (
                        <TableRow key={order._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {order.mealId?.name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.subscriptionId?.uid || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(order.deliveryDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              color={statusColors[order.status] || "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(order.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No orders found for this user.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Notification Dialog */}
        <NotificationDialog
          open={notificationDialog.open}
          onClose={handleCloseNotificationDialog}
          userId={notificationDialog.userId}
          userName={notificationDialog.userName}
        />
      </Box>
    </Layout>
  );
};

export default UserDetail;
